import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { organisationService, CreateOrganisationData, UpdateOrganisationData } from '../services/api/organisations';
import { perimeterService } from '../services/api/perimeters';
import { Organisation, Perimeter } from '../types/api';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks';

interface OrganisationFormData {
  name: string;
  displayName: string;
  description: string;
  perimeterId: number | null;
}

const Organisations: React.FC = () => {
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingOrg, setEditingOrg] = useState<Organisation | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<OrganisationFormData>({
    name: '',
    displayName: '',
    description: '',
    perimeterId: null,
  });

  // Vérifier les permissions
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  // Charger les périmètres pour le select
  const { data: perimeters = [] } = useQuery<Perimeter[]>({
    queryKey: ['perimeters', 'list'],
    queryFn: () => perimeterService.getPerimeters(),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    loadOrganisations();
  }, []);

  const loadOrganisations = async () => {
    try {
      setLoading(true);
      const data = await organisationService.getOrganisations();
      setOrganisations(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des organisations');
      console.error('Error loading organisations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.perimeterId) {
      setError('Veuillez sélectionner un périmètre');
      return;
    }
    
    try {
      let organisation;
      
      if (editingOrg) {
        // Update existing organisation via API
        const updateData: UpdateOrganisationData = {
          name: formData.name,
          displayName: formData.displayName,
          description: formData.description
        };
        organisation = await organisationService.updateOrganisation(editingOrg.id, updateData);
        
        // Refresh to get updated data
        await loadOrganisations();
      } else {
        // Create new organisation
        const createData: CreateOrganisationData = {
          name: formData.name,
          displayName: formData.displayName,
          description: formData.description,
          perimeterId: formData.perimeterId,
          isActive: true,
        };
        organisation = await organisationService.createOrganisation(createData);
        setOrganisations([...organisations, organisation]);
      }
      
      resetForm();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erreur lors de la sauvegarde');
      console.error('Error saving organisation:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ?')) {
      try {
        await organisationService.deleteOrganisation(id);
        setOrganisations(organisations.filter(org => org.id !== id));
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.error?.message || 'Erreur lors de la suppression');
        console.error('Error deleting organisation:', err);
      }
    }
  };

  const handleEdit = (org: Organisation) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      displayName: org.displayName,
      description: org.description || '',
      perimeterId: org.perimeterId || null,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      perimeterId: null,
    });
    setEditingOrg(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8">
            <div className="text-lg">Chargement des organisations...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-4 sm:px-6 sm:py-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Gestion des Organisations
          </h1>
          <p className="text-gray-600">
            Créez et gérez les organisations de votre système DigiManager
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="text-red-600 text-sm">{error}</div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-gray-600">
            {organisations.length} organisation(s) trouvée(s)
          </div>
          {isAdmin && (
            <Button
              onClick={() => setShowForm(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              + Nouvelle Organisation
            </Button>
          )}
        </div>

        {/* Organisation Form */}
        {showForm && (
          <div className="mb-8 px-4 py-4 sm:p-6 bg-white rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold mb-4">
              {editingOrg ? 'Modifier l\'organisation' : 'Nouvelle Organisation'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom technique"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ex: company-ltd"
                  required
                />
                <Input
                  label="Nom d'affichage"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  placeholder="ex: Company Ltd"
                  required
                />
              </div>

              {/* Sélecteur de périmètre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Périmètre *
                </label>
                <select
                  value={formData.perimeterId ?? ''}
                  onChange={(e) => setFormData({ ...formData, perimeterId: e.target.value ? Number(e.target.value) : null })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!!editingOrg}
                >
                  <option value="">— Sélectionner un périmètre —</option>
                  {perimeters.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description de l'organisation..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  type="submit"
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {editingOrg ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  className="w-full sm:w-auto bg-gray-500 hover:bg-gray-600 text-white"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Organisations List */}
        <div className="bg-transparent rounded-none shadow-none border-0">
          {organisations.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <div className="text-gray-500 mb-4">Aucune organisation trouvée</div>
              {isAdmin && (
                <Button
                  onClick={() => setShowForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Créer votre première organisation
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {organisations.map((org) => (
                <div
                  key={org.id}
                  className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {org.displayName}
                      </h3>
                      <div className="mt-1 text-sm text-gray-500 break-words">
                        {org.description || 'Aucune description'}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                          {org.name}
                        </span>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${org.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                        >
                          {org.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <div className="flex flex-col gap-1">
                          <Button
                            onClick={() => handleEdit(org)}
                            className="w-full sm:w-auto bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs px-3 py-1"
                          >
                            Modifier
                          </Button>
                          <Button
                            onClick={() => handleDelete(org.id)}
                            className="w-full sm:w-auto bg-red-100 hover:bg-red-200 text-red-700 text-xs px-3 py-1"
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Organisations;